import {
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Flex,
  type FlexProps,
  HStack,
  Input,
  Stack,
  type StackProps,
  Text,
} from '@chakra-ui/react';
import {
  Controller,
  type FieldError,
  type FieldPath,
  type FieldValues,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormFieldError } from '@/components/Form/FormFieldError';
import { FormFieldHelper } from '@/components/Form/FormFieldHelper';
import { FormFieldItem } from '@/components/Form/FormFieldItem';
import { FormFieldLabel } from '@/components/Form/FormFieldLabel';

import { type SelectionType } from '../FieldCreatableCheckBoxes';
import { type FieldCommonProps } from '../FormField.types';

export type CheckBoxFieldOption = {
  label: string;
  checked: boolean;
  value?: string;
  isOther?: boolean;
};

type CheckboxesProps = {
  selection: SelectionType;
  isOtherEnabled?: boolean;
  labelStyle?: CSSProperties;
  activeCheckboxStyle?: CSSProperties;
} & InputHTMLAttributes<HTMLInputElement>;

export type FieldCheckboxesProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  type: 'checkboxes';
  label?: ReactNode;
  helper?: ReactNode;
  rowGap?: FlexProps['rowGap'];
  columnGap?: FlexProps['columnGap'];
  direction?: FlexProps['direction'];
  options: Array<CheckBoxFieldOption>;
  children?: ReactNode;
  checkboxWrapperStyles?: StackProps;
} & FieldCommonProps<TFieldValues, TName> &
  CheckboxesProps;

export const FieldCheckboxes = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: FieldCheckboxesProps<TFieldValues, TName>
) => {
  const { t } = useTranslation(['interview', 'common']);

  const fieldValue = useWatch({
    control: props.control,
    name: props.name,
  });

  const hasFieldOptions = Array.isArray(fieldValue) && fieldValue.length > 0;

  const sourceOptions = hasFieldOptions ? fieldValue : props.options;

  const buildEditableOptions = () => {
    const allOptions: Array<CheckBoxFieldOption> = Array.isArray(sourceOptions)
      ? [...sourceOptions]
      : [];

    const baseOptions = allOptions.filter((o) => !o.isOther);
    const existingOthers = allOptions.filter((o) => o.isOther);

    if (!props.isOtherEnabled) {
      return baseOptions;
    }

    const otherOption =
      existingOthers[0] ??
      ({
        checked: false,
        label: t('common:other'),
        isOther: true,
      } as CheckBoxFieldOption);

    return [...baseOptions, otherOption];
  };

  const [editableOptions, setEditableOptions] = useState<
    Array<CheckBoxFieldOption>
  >(() => buildEditableOptions());

  // Sync editableOptions when sourceOptions or props change
  useEffect(() => {
    setEditableOptions(buildEditableOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(sourceOptions), props.isOtherEnabled]);

  const validateOtherInputNotEmpty = (options: Array<CheckBoxFieldOption>) => {
    const checkedOne = options.find((option) => option.checked);
    if (checkedOne?.isOther && !checkedOne.value) {
      return t(
        'interview:createNewInterview.questionPreparation.otherOptionShouldHaveValue'
      );
    }
    return true;
  };

  const validateAtLeastOneChecked = (options: Array<CheckBoxFieldOption>) => {
    return (
      options.some((option) => option.checked) ||
      t(
        'interview:createNewInterview.questionPreparation.atLeastOneOptionShouldBeSelected'
      )
    );
  };

  return (
    <Controller
      {...props}
      rules={{
        validate: {
          atLeastOneChecked: validateAtLeastOneChecked,
          otherNotEmpty: validateOtherInputNotEmpty,
        },
      }}
      render={({
        field: { onChange, ref: _ref, ...field },
        fieldState: { error },
        formState: { isSubmitting },
      }) => {
        const handleCheckedChange = (
          option: CheckBoxFieldOption,
          isChecked: boolean,
          inputValue?: string
        ) => {
          let updatedOptions: Array<CheckBoxFieldOption> = [];
          if (props.selection === 'single') {
            updatedOptions = editableOptions.map((o) =>
              o.value === option.value
                ? {
                    ...o,
                    checked: isChecked,
                    value: inputValue ?? o.value,
                  }
                : { ...o, checked: false }
            );
          }
          if (props.selection === 'multiple') {
            updatedOptions = editableOptions.map((o) =>
              o.value === option.value
                ? {
                    ...o,
                    checked: isChecked,
                    value: inputValue ?? o.value,
                  }
                : { ...o, checked: option.isOther ? false : o.checked }
            );

            const isOtherChecked = editableOptions.some(
              (c) => c.isOther && c.checked
            );
            const isAnyOptionsChecked = editableOptions.some(
              (c) => !c.isOther && c.checked
            );
            const isCurrentChecked = isChecked && !option.isOther;
            if (isOtherChecked && (isAnyOptionsChecked || isCurrentChecked)) {
              updatedOptions = updatedOptions.map((o) => ({
                ...o,
                checked: o.isOther ? false : o.checked,
              }));
            }
          }

          setEditableOptions(updatedOptions);
          onChange(updatedOptions);
        };

        return (
          <FormFieldItem>
            {!!props.label && <FormFieldLabel>{props.label}</FormFieldLabel>}
            {!!props.options && (
              <Flex
                columnGap={props.columnGap ?? 4}
                rowGap={props.rowGap ?? 1.5}
                direction={props.direction ?? 'column'}
                flexWrap="wrap"
              >
                {editableOptions.map((option) => (
                  <HStack
                    key={option.value}
                    {...(props.checkboxWrapperStyles ?? {})}
                  >
                    <CheckBoxInput
                      option={option}
                      selection={props.selection}
                      labelStyle={props.labelStyle}
                      activeCheckboxStyle={
                        option.isOther
                          ? {
                              ...props.activeCheckboxStyle,
                              background: 'transparent',
                              color: '#2F3236',
                            }
                          : props.activeCheckboxStyle
                      }
                      handleCheckedChange={handleCheckedChange}
                      {...field}
                      isOtherEnabled={option.isOther}
                      error={error}
                      isSubmitting={isSubmitting}
                    />
                  </HStack>
                ))}
              </Flex>
            )}
            {props.children}
            {!!props.helper && (
              <FormFieldHelper>{props.helper}</FormFieldHelper>
            )}
            <Stack mx={5}>
              <FormFieldError />
            </Stack>
          </FormFieldItem>
        );
      }}
    />
  );
};

type CheckBoxInputProps = {
  option: CheckBoxFieldOption;
  selection: SelectionType;
  isSubmitting: boolean;
  isOtherEnabled?: boolean;
  labelStyle?: CSSProperties;
  activeCheckboxStyle?: CSSProperties;
  error: FieldError | undefined;
  handleCheckedChange: (
    option: CheckBoxFieldOption,
    isChecked: boolean,
    inputValue?: string
  ) => void;
};

const CheckBoxInput: React.FC<CheckBoxInputProps> = (props) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [otherInputValue, setOtherInputValue] = useState(
    props.option.value ?? ''
  );

  useEffect(() => {
    if (props.option.isOther && !props.option.value && props.option.checked) {
      inputRef.current?.focus();
    }
  }, [
    props.isSubmitting,
    props.option.checked,
    props.option.isOther,
    props.option.value,
  ]);

  return (
    <HStack w="100%">
      <label
        className={`custom-checkbox-${props.selection}`}
        style={
          props.option.checked
            ? {
                ...props.activeCheckboxStyle,
                ...props.labelStyle,
              }
            : props.labelStyle
        }
      >
        <input
          checked={props.option.checked}
          onChange={(e) => {
            props.handleCheckedChange(props.option, e.target.checked);
            if (props.isOtherEnabled && !props.option.checked) {
              inputRef.current?.focus();
            }
          }}
          type="checkbox"
        />
        <span />
        <Text cursor="pointer">{props.option.label}</Text>
      </label>

      {props.isOtherEnabled && (
        <Input
          ref={inputRef}
          variant="unstyled"
          marginRight={5}
          paddingBottom={0}
          height={8}
          value={otherInputValue}
          onChange={(e) => setOtherInputValue(e.target.value)}
          onBlur={() =>
            props.option.checked &&
            props.handleCheckedChange(props.option, true, otherInputValue)
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              props.handleCheckedChange(props.option, true, otherInputValue);
              inputRef.current?.blur();
            }
          }}
          sx={{
            _focus: {
              borderBottomWidth: 1,
              borderColor:
                props.error?.type === 'otherNotEmpty'
                  ? 'red.500'
                  : 'primary.500',
              borderRadius: 'none',
            },
          }}
        />
      )}
    </HStack>
  );
};
